import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { blogAPI } from '../../lib/api';
import { marked } from 'marked';
import { 
    Calendar, 
    User, 
    Eye,
    ArrowLeft,
    BookOpen,
    Share2,
    Twitter,
    Linkedin,
    Facebook
} from 'lucide-react';

// Configure marked options
marked.setOptions({
    breaks: true,
    gfm: true
});

const BlogPostPage = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPost();
    }, [slug]);

    const fetchPost = async () => {
        try {
            setLoading(true);
            const response = await blogAPI.getPost(slug);
            setPost(response.data.post);
        } catch (error) {
            console.error('Error fetching post:', error);
            setError('Post not found');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getCategoryColor = (category) => {
        const colors = {
            'career-tips': 'bg-blue-100 text-blue-700',
            'industry-news': 'bg-purple-100 text-purple-700',
            'job-search': 'bg-green-100 text-green-700',
            'interviews': 'bg-amber-100 text-amber-700',
            'workplace': 'bg-rose-100 text-rose-700',
        };
        return colors[category] || 'bg-neutral-100 text-neutral-700';
    };

    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
    const shareTitle = post?.title || '';

    if (loading) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-16">
                    <div className="animate-pulse space-y-8">
                        <div className="h-8 bg-neutral-200 rounded w-3/4" />
                        <div className="h-64 bg-neutral-200 rounded-2xl" />
                        <div className="space-y-4">
                            <div className="h-4 bg-neutral-200 rounded" />
                            <div className="h-4 bg-neutral-200 rounded" />
                            <div className="h-4 bg-neutral-200 rounded w-2/3" />
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error || !post) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto px-4 py-16 text-center">
                    <BookOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-neutral-700 mb-2">Post Not Found</h1>
                    <p className="text-neutral-500 mb-8">The article you're looking for doesn't exist.</p>
                    <Button asChild>
                        <Link to="/blog">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Blog
                        </Link>
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Hero */}
            <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link 
                        to="/blog" 
                        className="inline-flex items-center gap-2 text-primary-200 hover:text-white mb-6 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Blog
                    </Link>
                    <Badge className={`${getCategoryColor(post.category)} mb-4`}>
                        {post.category.replace('-', ' ')}
                    </Badge>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                        {post.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-6 text-sm text-primary-200">
                        <span className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {post.author_name}
                        </span>
                        <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {formatDate(post.published_at)}
                        </span>
                        <span className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            {post.views} views
                        </span>
                    </div>
                </div>
            </section>

            {/* Content */}
            <article className="py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Cover Image */}
                    {(post.cover_image || post.featured_image) && (
                        <div className="mb-10 -mt-20 relative z-10">
                            <img
                                src={post.cover_image || post.featured_image}
                                alt={post.title}
                                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
                            />
                        </div>
                    )}

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-8">
                            {post.tags.map((tag) => (
                                <Badge key={tag} variant="outline" className="text-sm">
                                    #{tag}
                                </Badge>
                            ))}
                        </div>
                    )}

                    {/* Article Content */}
                    <div 
                        className="prose prose-lg max-w-none 
                            prose-headings:text-neutral-900 prose-headings:font-bold prose-headings:my-4
                            prose-p:text-neutral-700 prose-p:leading-relaxed prose-p:my-4 
                            prose-a:text-primary-600 prose-a:underline hover:prose-a:text-primary-800 
                            prose-strong:text-neutral-900 prose-strong:font-bold 
                            prose-ul:list-disc prose-ul:my-4 prose-ol:list-decimal prose-ol:my-4
                            prose-li:my-2
                            prose-img:rounded-lg prose-img:shadow-md prose-img:my-6
                            prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-6"
                        dangerouslySetInnerHTML={{
                            __html: /<\/?(p|h[1-6]|ul|ol|li|img|strong|em|blockquote|a|br|div|figure|table)\b/i.test(post.content || '')
                                ? post.content
                                : marked.parse(post.content || '')
                        }}
                    />

                    {/* Share */}
                    <div className="border-t border-neutral-200 mt-12 pt-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-neutral-600">
                                <Share2 className="w-5 h-5" />
                                <span className="font-medium">Share this article</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <a
                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 rounded-full bg-neutral-100 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                                >
                                    <Twitter className="w-5 h-5" />
                                </a>
                                <a
                                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 rounded-full bg-neutral-100 hover:bg-blue-100 hover:text-blue-700 transition-colors"
                                >
                                    <Linkedin className="w-5 h-5" />
                                </a>
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 rounded-full bg-neutral-100 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                                >
                                    <Facebook className="w-5 h-5" />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="bg-primary-50 rounded-2xl p-8 mt-12 text-center">
                        <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                            Ready to find your next opportunity?
                        </h3>
                        <p className="text-neutral-600 mb-6">
                            Connect with top recruitment agencies in your area today.
                        </p>
                        <Button asChild className="bg-primary-600 hover:bg-primary-700">
                            <Link to="/agencies">Find Recruiters Near Me</Link>
                        </Button>
                    </div>
                </div>
            </article>
        </Layout>
    );
};

export default BlogPostPage;
